<script setup lang="ts">
  import { CheckCircleIcon, XCircleIcon } from '@heroicons/vue/24/solid'
  import AudioPlaybackButton from '@/components/admin/AudioPlaybackButton.vue'

  interface GeminiEntity {
    value: string | null
    timestamp: string | null
    end_timestamp: string | null
  }

  interface GptRecordingTimestamps {
    first_name_recording_start_seconds: string | null
    first_name_recording_end_seconds: string | null
    last_name_recording_start_seconds: string | null
    last_name_recording_end_seconds: string | null
    email_recording_start_seconds: string | null
    email_recording_end_seconds: string | null
    phone_number_recording_start_seconds: string | null
    phone_number_recording_end_seconds: string | null
  }

  interface GeminiShadowComparison {
    gpt_first_name: string | null
    gpt_last_name: string | null
    gpt_email: string | null
    gpt_phone: string | null
    gemini_first_name: GeminiEntity
    gemini_last_name: GeminiEntity
    gemini_email: GeminiEntity
    gemini_phone: GeminiEntity
    match_first_name: boolean
    match_last_name: boolean
    match_email: boolean
    match_phone: boolean
  }

  const props = defineProps<{
    comparison: GeminiShadowComparison
    audioUrl: string | null
    gptTimestamps: GptRecordingTimestamps | null
  }>()

  const fields = [
    { key: 'first_name', label: 'First Name' },
    { key: 'last_name', label: 'Last Name' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' }
  ] as const

  type FieldKey = (typeof fields)[number]['key']

  const getGptValue = (key: FieldKey): string | null => {
    const mapping: Record<FieldKey, keyof GeminiShadowComparison> = {
      first_name: 'gpt_first_name',
      last_name: 'gpt_last_name',
      email: 'gpt_email',
      phone: 'gpt_phone'
    }
    return props.comparison[mapping[key]] as string | null
  }

  const getGeminiEntity = (key: FieldKey): GeminiEntity => {
    const mapping: Record<FieldKey, keyof GeminiShadowComparison> = {
      first_name: 'gemini_first_name',
      last_name: 'gemini_last_name',
      email: 'gemini_email',
      phone: 'gemini_phone'
    }
    return props.comparison[mapping[key]] as GeminiEntity
  }

  const getMatch = (key: FieldKey): boolean => {
    const mapping: Record<FieldKey, keyof GeminiShadowComparison> = {
      first_name: 'match_first_name',
      last_name: 'match_last_name',
      email: 'match_email',
      phone: 'match_phone'
    }
    return props.comparison[mapping[key]] as boolean
  }

  // Convert "MM:SS" format to seconds
  const parseTimestamp = (timestamp: string | null): string | null => {
    if (!timestamp) return null
    const parts = timestamp.split(':')
    if (parts.length !== 2) return null
    const minutes = parseInt(parts[0], 10)
    const seconds = parseInt(parts[1], 10)
    if (isNaN(minutes) || isNaN(seconds)) return null
    return String(minutes * 60 + seconds)
  }

  // Get GPT recording timestamps for a field
  const getGptTimestamps = (key: FieldKey): { start: string | null; end: string | null } => {
    if (!props.gptTimestamps) return { start: null, end: null }

    const mapping: Record<
      FieldKey,
      { start: keyof GptRecordingTimestamps; end: keyof GptRecordingTimestamps }
    > = {
      first_name: {
        start: 'first_name_recording_start_seconds',
        end: 'first_name_recording_end_seconds'
      },
      last_name: {
        start: 'last_name_recording_start_seconds',
        end: 'last_name_recording_end_seconds'
      },
      email: {
        start: 'email_recording_start_seconds',
        end: 'email_recording_end_seconds'
      },
      phone: {
        start: 'phone_number_recording_start_seconds',
        end: 'phone_number_recording_end_seconds'
      }
    }

    return {
      start: props.gptTimestamps[mapping[key].start],
      end: props.gptTimestamps[mapping[key].end]
    }
  }

  // Check if GPT audio can be played for a field
  const canPlayGptAudio = (key: FieldKey): boolean => {
    if (!props.audioUrl) return false
    const timestamps = getGptTimestamps(key)
    return timestamps.start !== null && timestamps.end !== null
  }

  // Check if Gemini audio can be played
  const canPlayGeminiAudio = (entity: GeminiEntity): boolean => {
    return !!props.audioUrl && !!entity.timestamp && !!entity.end_timestamp
  }
</script>

<template>
  <div class="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
    <h3 class="mb-4 text-lg font-semibold text-gray-800">Entity Extraction Comparison</h3>

    <div class="overflow-x-auto">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th
              scope="col"
              class="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
            >
              Field
            </th>
            <th
              scope="col"
              class="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
            >
              GPT (Production)
            </th>
            <th
              scope="col"
              class="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500"
            >
              GPT Audio
            </th>
            <th
              scope="col"
              class="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
            >
              Gemini (Shadow)
            </th>
            <th
              scope="col"
              class="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500"
            >
              Gemini Audio
            </th>
            <th
              scope="col"
              class="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500"
            >
              Match
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-200 bg-white">
          <tr
            v-for="field in fields"
            :key="field.key"
          >
            <td class="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">
              {{ field.label }}
            </td>
            <!-- GPT Value -->
            <td class="px-4 py-3 text-sm text-gray-700">
              <span v-if="getGptValue(field.key)">{{ getGptValue(field.key) }}</span>
              <span
                v-else
                class="text-gray-400"
              >
                -
              </span>
            </td>
            <!-- GPT Audio -->
            <td class="whitespace-nowrap px-4 py-3 text-center">
              <AudioPlaybackButton
                v-if="canPlayGptAudio(field.key)"
                :audioUrl="audioUrl ?? ''"
                :startTime="getGptTimestamps(field.key).start ?? ''"
                :endTime="getGptTimestamps(field.key).end ?? ''"
              />
              <span
                v-else
                class="text-gray-400"
              >
                -
              </span>
            </td>
            <!-- Gemini Value -->
            <td class="px-4 py-3 text-sm text-gray-700">
              <div v-if="getGeminiEntity(field.key)?.value">
                <div>{{ getGeminiEntity(field.key).value }}</div>
                <div
                  v-if="getGeminiEntity(field.key).timestamp"
                  class="text-xs text-gray-400"
                >
                  {{ getGeminiEntity(field.key).timestamp }}
                  <template v-if="getGeminiEntity(field.key).end_timestamp">
                    - {{ getGeminiEntity(field.key).end_timestamp }}
                  </template>
                </div>
              </div>
              <span
                v-else
                class="text-gray-400"
              >
                -
              </span>
            </td>
            <!-- Gemini Audio -->
            <td class="whitespace-nowrap px-4 py-3 text-center">
              <AudioPlaybackButton
                v-if="canPlayGeminiAudio(getGeminiEntity(field.key))"
                :audioUrl="audioUrl ?? ''"
                :startTime="parseTimestamp(getGeminiEntity(field.key).timestamp) ?? ''"
                :endTime="parseTimestamp(getGeminiEntity(field.key).end_timestamp) ?? ''"
              />
              <span
                v-else
                class="text-gray-400"
              >
                -
              </span>
            </td>
            <!-- Match -->
            <td class="whitespace-nowrap px-4 py-3 text-center">
              <CheckCircleIcon
                v-if="getMatch(field.key)"
                class="inline-block h-5 w-5 text-green-500"
              />
              <XCircleIcon
                v-else
                class="inline-block h-5 w-5 text-red-500"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
